import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo, useRef } from 'react';
import { BookContents } from '~/views/molecules/book-contents';
import { BookSheetContent } from '~/views/molecules/book-sheet-content';
import { BookCover } from '~/views/molecules/book-cover';
import { getCoverLink } from '~/tools/file';
import { EBreadcrumbKey, EFileSize } from '~/shared/types';
import { useActions } from '~/pages/funds/books/cover-book/useActions';
import { useService } from '~/services/service.base';
import { ServiceBooks } from '~/services/service.books';
import { ServiceConfig } from '~/services/service.config';
import { PageWrapper } from '~/views/organisms/wrapper.page';
import { Breadcrumb } from '~/views/molecules/nav.breadcrumbs';
import { PageHeader } from '~/views/molecules/page.header';
import { Grid, IconButton, Typography } from '@mui/material';
import { CloseButton } from '~/views/molecules/dialog-buttons/close-button';
import { BookIcon } from '~/views/atoms/svg-icons';
import { t } from 'i18next';
import { Column } from '~/views/atoms/boxes';
import { UIService } from '~/services/service.ui';
export const useElements = () => {
    const { booksCoverService, clickByImageHandler, closeBookContentHandler, clickBySectionHandler, serviceBookContent, changeSectionHandler, openBookHandler, communityService, } = useActions();
    const booksService = useService(ServiceBooks);
    const serviceConfig = useService(ServiceConfig);
    const { breadcrumbsSvc } = useService(UIService);
    const initialized = useRef(false);
    useEffect(() => {
        if (!initialized.current) {
            booksCoverService.clearData();
            serviceBookContent.clearData();
        }
        initialized.current = true;
    }, [booksCoverService, serviceBookContent]);
    useEffect(() => {
        breadcrumbsSvc.use([
            {
                key: EBreadcrumbKey.recent,
            },
            {
                key: EBreadcrumbKey.books,
            },
        ]);
    }, [breadcrumbsSvc]);
    const form = useMemo(() => {
        if (booksCoverService.openContentService.open) {
            return (_jsx(BookContents, { pagesWithoutChapter: booksCoverService.pagesWithoutChapter.toJSON(), chapters: booksCoverService.chapters.toJSON(), clickBySectionHandler: clickBySectionHandler }));
        }
        else if (booksCoverService.openSectionService.open) {
            return (_jsx(BookSheetContent, { currentSheet: booksCoverService.currentSheet, nextSheet: booksCoverService.nextSheet, prevSheet: booksCoverService.prevSheet, documents: serviceBookContent.documents, sections: serviceBookContent.sections, switchToNextSection: changeSectionHandler, switchToPrevSection: changeSectionHandler, clickByThumbHandler: clickByImageHandler }));
        }
        else {
            return (_jsx(BookCover, { coverLink: getCoverLink(booksService.openedBook?.coverLink, serviceConfig.awsUrl, communityService.currentCommunity?.coverLink || '', `_${EFileSize.big}`), book: booksService.openedBook, sheets: booksCoverService.sheets, openBookHandler: openBookHandler }));
        }
    }, [
        booksCoverService.chapters,
        booksCoverService.currentSheet,
        booksCoverService.nextSheet,
        booksCoverService.openContentService.open,
        booksCoverService.openSectionService.open,
        booksCoverService.pagesWithoutChapter,
        booksCoverService.prevSheet,
        booksCoverService.sheets,
        booksService.openedBook,
        changeSectionHandler,
        clickByImageHandler,
        clickBySectionHandler,
        communityService.currentCommunity?.coverLink,
        openBookHandler,
        serviceBookContent.documents,
        serviceBookContent.sections,
        serviceConfig.awsUrl,
    ]);
    const content = useMemo(() => (_jsxs(PageWrapper, { children: [_jsx(Breadcrumb.Nav, { children: breadcrumbsSvc.children }), _jsxs(PageHeader, { flexDirection: "column", justifyContent: "flex-start", children: [_jsxs(Grid, { container: true, direction: "row", children: [_jsx(Grid, { container: true, item: true, xs: 9, justifyContent: "center", flexDirection: "column", children: _jsx(Typography, { variant: "h2", children: booksService.openedBook?.name || '' }) }), _jsx(Grid, { container: true, item: true, xs: 3, justifyContent: "center", flexDirection: "column", children: _jsx(Grid, { container: true, minHeight: 40, justifyContent: "flex-end", children: booksCoverService.openContentService.open ? (_jsx(CloseButton, { closeButtonHandler: closeBookContentHandler })) : (_jsx(IconButton, { color: "inherit", onClick: openBookHandler, children: _jsx(BookIcon, { color: "primary" }) })) }) })] }), _jsx(Grid, { container: true, direction: "row", children: _jsxs(Grid, { container: true, item: true, justifyContent: "center", flexDirection: "column", children: [_jsxs(Typography, { variant: "body2", children: [t('BOOK_COVER_PAGE_OWNER'), " ", booksService.openedBook?.ownerName] }), _jsxs(Typography, { variant: "body2", children: [t('BOOK_COVER_PAGE_DATE'), " ", booksService.openedBook?.formattedCDate] })] }) })] }), _jsx(Column, { justifyContent: "flex-start", height: "100%", gap: 2, children: form })] })), [
        booksCoverService.openContentService.open,
        booksService.openedBook?.formattedCDate,
        booksService.openedBook?.name,
        booksService.openedBook?.ownerName,
        breadcrumbsSvc.children,
        closeBookContentHandler,
        form,
        openBookHandler,
    ]);
    return {
        content,
    };
};
